import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  creditcardConfirmSetupApi,
  creditcardListApi,
  creditcardReconcileSubscriptionApi,
  creditcardStartSetupApi,
} from "store/slices/creditcardUpdateSlice";
import { contentErrorMessage } from "store/slices/contentError";
import {
  buildClientPaymentMethodConfirmation,
  buildClientPaymentMethodSetup,
  getConfirmedSetupIntentId,
  getSetupIntentClientSecret,
  normalizeSavedCards,
  subscriptionCardNotice,
} from "./clientPaymentMethodsContract";

// HandleError rejects with the raw response body as `message`, so an object
// body can land in state and be handed to React as a child, which white-screens
// the saved-cards panel. Resolve renderable copy before it reaches render.
const paymentErrorMessage = (value, fallback) => {
  const message = contentErrorMessage({ payload: value }, fallback);
  return typeof message === "string" && message.trim() ? message : fallback;
};

const ClientPaymentMethodsForm = ({ clientId }) => {
  const dispatch = useDispatch();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [reconciliationFailed, setReconciliationFailed] = useState(false);
  const cardState = useSelector((state) => state.creditcard);
  const ownsCards = cardState.savedCardsClientId === String(clientId);
  const cards = ownsCards ? normalizeSavedCards(cardState.isCreditCardListData) : [];
  const subscriptionNotice = ownsCards ? subscriptionCardNotice(reconciliationFailed ? {subscription_card_reconciliation:{status:"unavailable"}} : cardState.isCreditCardListData) : "";
  const listError = ownsCards ? cardState.isCreditCardListDataError : "";
  const listStatus = ownsCards ? cardState.isApiStatus?.creditcardListApi : "";
  const loadingCards = !listError && listStatus !== "succeeded" && listStatus !== "failed";

  useEffect(() => {
    if (!clientId) return undefined;
    let stopped = false;
    let fallback;
    const request = dispatch(creditcardReconcileSubscriptionApi({ client_id: clientId }));
    Promise.resolve(request).then(action => {
      if (!stopped && action?.meta?.requestStatus !== "fulfilled") {
        setReconciliationFailed(Number(action?.payload?.status) !== 403);
        fallback = dispatch(creditcardListApi({ client_id: clientId }));
      }
    });
    return () => { stopped = true; request?.abort?.(); fallback?.abort?.(); };
  }, [dispatch, clientId]);

  const saveCard = async (event) => {
    event.preventDefault();
    setError("");
    if (!stripe || !elements) {
      setError("Card payments are not available right now.");
      return;
    }
    const card = elements.getElement(CardElement);
    if (!card) {
      setError("Enter valid card details before saving.");
      return;
    }
    setSaving(true);
    try {
      const startSetupAction = await dispatch(
        creditcardStartSetupApi(buildClientPaymentMethodSetup(clientId)),
      );
      if (!active.current) return;
      if (startSetupAction.meta.requestStatus !== "fulfilled") {
        setError(paymentErrorMessage(startSetupAction.payload, "Card setup could not be started."));
        return;
      }
      const clientSecret = getSetupIntentClientSecret(startSetupAction.payload);
      if (!clientSecret) {
        setError("Card setup could not be started.");
        return;
      }
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (!active.current) return;
      const setupIntentId = getConfirmedSetupIntentId(result);
      if (result.error || !setupIntentId) {
        setError(paymentErrorMessage(result.error, "The card could not be saved."));
        return;
      }
      const action = await dispatch(creditcardConfirmSetupApi(
        buildClientPaymentMethodConfirmation(clientId, setupIntentId),
      ));
      if (!active.current) return;
      if (action.meta.requestStatus === "fulfilled") {
        dispatch(creditcardListApi({ client_id: clientId }));
        card.clear();
      } else {
        setError(paymentErrorMessage(action.payload, "The card could not be saved."));
      }
    } catch (failure) {
      if (active.current) setError(paymentErrorMessage(failure, "The card could not be saved."));
    } finally {
      if (active.current) setSaving(false);
    }
  };

  return (
    <div className="content-wrp">
      <h2 className="mb-4">Saved cards</h2>
      {loadingCards ? <p role="status">Loading saved cards…</p> : cards.length > 0 ? cards.map((card) => (
        <div className="voucher-box p-3 mb-3" key={card.id}>
          <strong>{card.brand} **** {card.last4}</strong>
          {card.exp_month && card.exp_year ? <span className="ms-2">Expires {card.exp_month}/{card.exp_year}</span> : null}
          {card.is_default ? <span className="badge bg-secondary ms-2">Default</span> : null}
        </div>
      )) : listStatus === "succeeded" && !listError && !subscriptionNotice ? <p>No saved cards.</p> : null}
      {!loadingCards && subscriptionNotice && <div className="alert alert-warning" role="alert">{subscriptionNotice}</div>}
      <form onSubmit={saveCard} noValidate>
        <label htmlFor="client-card-element">Add a card</label>
        <div id="client-card-element" className="form-control mb-3 p-3">
          <CardElement options={{ disableLink: true }} />
        </div>
        {(error || listError) && <div className="alert alert-danger" role="alert">{error || paymentErrorMessage(listError, "Saved cards could not be loaded.")}</div>}
        <button type="submit" className="btn btn-primary" disabled={saving || !clientId}>
          {saving ? "Saving..." : "Save card"}
        </button>
      </form>
    </div>
  );
};

const ClientPaymentMethods = ({ clientId }) => <ClientPaymentMethodsForm key={String(clientId)} clientId={clientId} />;

ClientPaymentMethodsForm.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

ClientPaymentMethods.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default ClientPaymentMethods;
